import { toRosterMemberKey } from '@sorrem-client/ui/shared/roster-member-key';
import type { PartyPanelRow, PartyPanelRowAction } from '@sorrem-client/ui/features/Hud';
import { useAppNavigate, useConfirm } from '@sorrem-client/ui/hooks';
import type { CrucibleMember } from '@sorrem/shared';
import { useCallback } from 'react';

interface PartyMembershipActionsInput {
    readonly instanceId: string | null;
    readonly isCreator: boolean;
    readonly isStarting: boolean;
    readonly members: readonly CrucibleMember[];
    /** The viewer's own party row, or null while they aren't in the party. */
    readonly selfRowKey: string | null;
    readonly leaveCrucibleParty: () => Promise<boolean>;
    readonly removeCrucibleParty: () => Promise<boolean>;
    readonly removeMember: (characterId: string) => Promise<boolean>;
}

/**
 * The party panel's row menus: the viewer's own row offers Leave (the
 * leader may close the crucible instead); the leader's other rows offer
 * Remove; nobody else gets a menu. Each asks for confirmation first.
 */
export function usePartyMembershipActions({
    instanceId,
    isCreator,
    isStarting,
    members,
    selfRowKey,
    leaveCrucibleParty,
    removeCrucibleParty,
    removeMember,
}: PartyMembershipActionsInput): (row: PartyPanelRow) => readonly PartyPanelRowAction[] {
    const navigate = useAppNavigate();
    const { choose, confirm } = useConfirm();

    const handleLeave = useCallback(async (): Promise<void> => {
        if (!instanceId) {
            navigate('/character/crucibles');
            return;
        }

        let shouldRemove = false;
        if (isCreator) {
            const choice = await choose({
                title: 'Leave this crucible?',
                description: 'Leave keeps the crucible open for others while you step out of the party. Remove closes it for everyone currently in the party.',
                cancelLabel: 'Cancel',
                choices: [
                    { id: 'leave', label: 'Leave' },
                    { id: 'remove', label: 'Remove' },
                ],
            });

            if (choice === null) {
                return;
            }
            shouldRemove = choice === 'remove';
        } else {
            const didConfirm = await confirm({
                title: 'Leave this crucible party?',
                description: 'You can join again later from the open party list.',
                confirmLabel: 'Yes',
                cancelLabel: 'No',
            });

            if (!didConfirm) {
                return;
            }
        }

        const didLeave = shouldRemove
            ? await removeCrucibleParty()
            : await leaveCrucibleParty();

        if (didLeave) {
            navigate('/character/crucibles');
        }
    }, [choose, confirm, instanceId, isCreator, leaveCrucibleParty, navigate, removeCrucibleParty]);

    const handleRemoveMember = useCallback(async (characterId: string): Promise<void> => {
        const target = members.find((member) => member.characterId === characterId);
        const label = target?.characterName ?? target?.displayName ?? 'this player';

        const didConfirm = await confirm({
            title: `Remove ${label} from the party?`,
            description: 'They can rejoin later from the open party list.',
            confirmLabel: 'Remove',
            cancelLabel: 'Cancel',
        });

        if (!didConfirm) {
            return;
        }

        void removeMember(characterId);
    }, [confirm, members, removeMember]);

    return useCallback((row: PartyPanelRow): readonly PartyPanelRowAction[] => {
        if (row.key === selfRowKey) {
            return [{ label: 'Leave party', onSelect: (): void => { void handleLeave(); } }];
        }
        // Rows are keyed by account and character (resolveGatheringPartyRows).
        const member = members.find((entry) => toRosterMemberKey(entry) === row.key);
        if (!isCreator || member === undefined) {
            return [];
        }
        return [{ label: 'Remove from party', disabled: isStarting, onSelect: (): void => { void handleRemoveMember(member.characterId); } }];
    }, [handleLeave, handleRemoveMember, isCreator, isStarting, members, selfRowKey]);
}
